import { useState, useCallback, useRef, useEffect } from 'react';
import { accountService } from '@/features/card/services/accountService/accountService';
import type { AccountDetail, AccountListRequest } from '@/features/card/types/account.types';
import type { Card } from '@/features/card/types/card.types';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useAsyncGuard } from '@chikara/hooks';
import { useSpinner } from '@chikara/ui';
import { t } from '@/i18n';
import { formatDateTimeExact } from '@chikara/utils';
import { formatCardAliasForDisplay, formatCardLabelForDisplay } from '@/features/card/utils/cardDisplayLabels';

export const useAccounts = () => {
    const { payload: authPayload } = useAuth();
    const { show, hide, isSpinnerLoading } = useSpinner();
    const { checkGuard } = useAsyncGuard();

    const [accounts, setAccounts] = useState<Card[]>([]);
    const [currentStatus, setCurrentStatus] = useState<string | undefined>('pending_approval');
    const [error, setError] = useState<string | null>(null);
    const [paginationData, setPaginationData] = useState({
        currentPage: 1,
        totalPages: 1,
        itemsPerPage: 10,
        totalItems: 0,
    });

    const authRef = useRef({
        userUuid: authPayload?.user_uuid,
    });

    useEffect(() => {
        authRef.current = {
            userUuid: authPayload?.user_uuid,
        };
    }, [authPayload?.user_uuid]);

    const fetchAccounts = useCallback(async (page: number = 1, limit: number = 10, status?: string) => {
        if (!checkGuard()) return;

        show();
        setError(null);
        try {
            if (status !== undefined) {
                setCurrentStatus(status);
            }

            const params: AccountListRequest = {
                page,
                limit,
                status: status || currentStatus,
                balance: true,
            };

            const response = await accountService.listAccounts(params);

            const accountList = response.data.accounts.map((item: AccountDetail) => {
                return {
                    ...item,
                    email: item.user?.email,
                    cardAlias: formatCardAliasForDisplay(item.card?.metadata?.alias),
                    maskedPan: item.card?.pan_masked ? `•••• ${String(item.card?.pan_masked).slice(-4)}` : '•••• 0000',
                    cardholderName: item.user?.fullname,
                    disposable: item.balance?.disposable || 0,
                    creditLimit: item.balance?.limit_assigned || 0,
                    type: item.card?.type || 'N/A',
                    tag: formatCardLabelForDisplay(item.card?.metadata?.tag),
                    status: item.status,
                    cdate: formatDateTimeExact(item.cdate),
                    card_uuid: item.card?.uuid,
                    card_status: item.card?.status,
                    last_activity: item.card?.last_activity || 'N/A',
                }
            });

            setAccounts(accountList || []);
            if (response.data.pagination) {
                setPaginationData({
                    currentPage: response.data.pagination.current_page,
                    totalPages: response.data.pagination.total_pages,
                    itemsPerPage: response.data.pagination.limit,
                    totalItems: response.data.pagination.total_records,
                });
            }
        } catch (err) {
            setError(t('card.error.getCards'));
            console.error(err);
        } finally {
            hide();
        }
    }, [show, hide, checkGuard]);

    const handlePageChange = useCallback((page: number, limit: number) => {
        fetchAccounts(page, limit, currentStatus);
    }, [fetchAccounts, currentStatus]);

    const pagination = {
        ...paginationData,
        onPageChange: handlePageChange
    };

    return {
        accounts,
        error,
        pagination,
        isSpinnerLoading,
        refetch: fetchAccounts,
    };
};
